import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API, PHOTO_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  CarIcon,
  CalendarIcon,
  GaugeIcon,
  GasPumpIcon,
  GearIcon,
  EngineIcon,
  PaletteIcon,
  CheckCircleIcon,
  CaretLeftIcon,
  CaretRightIcon,
  PrinterIcon,
  ArrowLeftIcon,
  StarIcon,
} from "@phosphor-icons/react";

const fmtKm = (n) => new Intl.NumberFormat("el-GR").format(n || 0) + " χλμ.";
const fmtEUR = (n) => new Intl.NumberFormat("el-GR").format(n || 0) + " €";

export default function Presentation() {
  const { id } = useParams();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/cars/${id}`);
        setCar(r.data);
      } catch {
        setCar(false);
      }
    })();
  }, [id]);

  if (car === null) return <div className="min-h-screen flex items-center justify-center bg-white text-sm">Φόρτωση...</div>;
  if (car === false) return <div className="min-h-screen flex items-center justify-center bg-white">Δεν βρέθηκε</div>;

  const photos = car.photos || [];
  const eq = car.equipment || [];

  const printBrochure = () => {
    // Only authenticated admin users get the server-rendered A4 PDF;
    // public visitors fall back to the browser print dialog.
    if (!user || user === false) {
      window.print();
      return;
    }
    window.open(`${API}/cars/${car.id}/brochure.pdf`, "_blank");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="presentation-page">
      {/* Top bar */}
      <header className="border-b border-black/10 bg-white sticky top-0 z-10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/inventory" className="p-2 hover:bg-black/5 -ml-2"><ArrowLeftIcon size={18} /></Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#E60000] flex items-center justify-center">
                <CarIcon size={18} weight="bold" color="#fff" />
              </div>
              <div>
                <div className="heading font-extrabold text-sm leading-none">BESIKOS CARS</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mt-0.5">Παρουσίαση</div>
              </div>
            </div>
          </div>
          <button onClick={printBrochure} className="btn-primary text-sm flex items-center gap-1.5" data-testid="print-brochure">
            <PrinterIcon size={16} /> Εκτύπωση A4
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Title + price */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end border-b border-black/10 pb-6">
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#E60000] font-medium flex items-center gap-2">
              {car.featured && <StarIcon size={12} weight="fill" />} ΜΕΤΑΧΕΙΡΙΣΜΕΝΟ
            </div>
            <h1 className="heading text-4xl md:text-5xl font-extrabold tracking-tight mt-1 leading-tight">
              {car.brand} {car.model}
            </h1>
            {car.variant && <div className="text-lg text-[#4B5563] mt-1">{car.variant}</div>}
            <div className="font-mono text-sm text-[#4B5563] mt-3 flex flex-wrap gap-x-4 gap-y-1">
              <span>{car.year}</span>
              <span>·</span>
              <span>{fmtKm(car.mileage_km)}</span>
              <span>·</span>
              <span>{car.fuel_type}</span>
              <span>·</span>
              <span>{car.transmission}</span>
            </div>
          </div>
          <div className="md:text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Τιμή</div>
            <div className="heading text-5xl md:text-6xl font-extrabold tracking-tight text-[#E60000]">
              {fmtEUR(car.price_eur)}
            </div>
          </div>
        </div>

        {/* Hero photo + thumbnails */}
        {photos.length > 0 && (
          <div>
            <div className="relative aspect-[16/9] bg-[#F9FAFB] border border-black/10 overflow-hidden">
              <img
                src={PHOTO_URL(photos[active].url)}
                alt=""
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => setActive((a) => (a - 1 + photos.length) % photos.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white border border-black/10 p-2 hover:bg-[#0A0A0A] hover:text-white transition-colors"
                  >
                    <CaretLeftIcon size={18} />
                  </button>
                  <button
                    onClick={() => setActive((a) => (a + 1) % photos.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white border border-black/10 p-2 hover:bg-[#0A0A0A] hover:text-white transition-colors"
                  >
                    <CaretRightIcon size={18} />
                  </button>
                </>
              )}
              <div className="absolute bottom-3 right-3 bg-[#0A0A0A] text-white text-xs font-mono px-2 py-1">
                {active + 1} / {photos.length}
              </div>
            </div>
            {photos.length > 1 && (
              <div className="mt-3 grid grid-cols-6 md:grid-cols-10 gap-2">
                {photos.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => setActive(i)}
                    className={`aspect-[4/3] overflow-hidden border ${i === active ? "border-[#E60000] border-2" : "border-black/10"}`}
                  >
                    <img src={PHOTO_URL(p.url)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Spec grid */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-3">Χαρακτηριστικά</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-black/10">
            <Spec icon={CalendarIcon} label="Έτος" value={car.year} />
            <Spec icon={GaugeIcon} label="Χιλιόμετρα" value={fmtKm(car.mileage_km)} />
            <Spec icon={GasPumpIcon} label="Καύσιμο" value={car.fuel_type} />
            <Spec icon={GearIcon} label="Κιβώτιο" value={car.transmission} />
            <Spec icon={EngineIcon} label="Κυβισμός" value={car.engine_cc ? `${car.engine_cc} cc` : "—"} />
            <Spec icon={EngineIcon} label="Ίπποι" value={car.horsepower ? `${car.horsepower} hp` : "—"} />
            <Spec icon={CarIcon} label="Αμάξωμα" value={car.body_type || "—"} />
            <Spec icon={PaletteIcon} label="Χρώμα" value={car.color_exterior || "—"} />
          </div>
        </div>

        {/* Description */}
        {car.description && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-2">Περιγραφή</div>
            <div className="prose prose-sm max-w-none whitespace-pre-line border border-black/10 p-6 bg-[#F9FAFB]">
              {car.description}
            </div>
          </div>
        )}

        {/* Equipment */}
        {eq.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-3">
              Εξοπλισμός ({eq.length})
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-y-1.5 gap-x-4">
              {eq.map((e) => (
                <div key={e} className="flex items-center gap-2 text-sm">
                  <CheckCircleIcon size={14} weight="fill" color="#16A34A" />
                  {e}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-black/10 pt-6 text-center">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">Βρείτε αυτό και άλλα οχήματα</div>
          <div className="heading text-2xl font-extrabold mt-1">BESIKOS CARS</div>
          {car.location && <div className="text-sm text-[#4B5563] mt-1">{car.location}</div>}
        </div>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="border-b border-r border-black/10 p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">
        <Icon size={14} />
        {label}
      </div>
      <div className="heading font-bold text-base mt-1">{value}</div>
    </div>
  );
}
